/**
 * 路線形状 GeoJSON のフェッチ。
 *
 * - 線形データは「方面」(routeDirection) ごとに 1 ファイルとして
 *   `web-app/public/data/route_shapes/<slug>.geojson` に同梱されている。
 * - 各 Feature は GPS 観測点をサンプルした **Point** ジオメトリで、
 *   properties に GPS 精度 (`gps_accuracy_avg/min/max`) や
 *   アナウンス有無 (`is_announcing`) などの計測値を持つ。
 * - ファイルが存在しない (= 旧デプロイや未対応路線) 場合は `null` を返し、
 *   呼び出し側で「線形データを表示しない」仕様とする。
 *
 * Issue #231: 以前は `${route_id}_${direction_name}.geojson` (日本語ファイル名)
 *   としていたが、CDK `BucketDeployment` の内蔵 Lambda がロケール `C` で
 *   起動されるため AWS CLI (`aws s3 sync`) が UnicodeEncodeError で失敗する。
 *   このため、ファイル名は ASCII のみのスラッグに正規化し、
 *   `(routeId, directionName)` からスラッグを解決するマッピングを持つ。
 */

/**
 * route_shapes/*.geojson の各 Feature の properties。
 *
 * すべての値は欠損 (null) する可能性がある (まだ計測点が取れていない時)。
 */
export interface RouteShapePointProperties {
  route?: string;
  direction?: string;
  from_station?: string;
  to_station?: string;
  sequence?: number;
  /** GPS 精度 (m) の平均。値が小さいほど高精度。 */
  gps_accuracy_avg: number | null;
  gps_accuracy_min: number | null;
  gps_accuracy_max: number | null;
  /** GPS マッチした計測サンプル数 */
  gps_match_count: number | null;
  /**
   * 当該地点でアナウンスが行われた実績。
   * - `true`: アナウンス区間 (赤系で表示)
   * - `false`: 非アナウンス区間 (灰系で表示)
   * - `null`: 未取得 (薄灰で表示)
   */
  is_announcing: boolean | null;
  recorded_at: string | null;
}

export interface RouteShapePointFeature {
  type: 'Feature';
  properties: RouteShapePointProperties;
  geometry: {
    type: 'Point';
    /** [lng, lat] (GeoJSON 規約) */
    coordinates: [number, number];
  };
}

/** 方面ごとの Point Feature コレクション */
export interface RouteShapePointsFile {
  type: 'FeatureCollection';
  name?: string;
  features: RouteShapePointFeature[];
}

/**
 * `(routeId, directionName)` → ASCII 化した GeoJSON ファイル名 (拡張子除く) の
 * 明示的マッピング。
 *
 * Issue #231: `route_shapes/` 配下のファイル名に日本語を含めると
 *   CDK `BucketDeployment` が起動する Python Lambda 内の `aws s3 sync` が
 *   `UnicodeEncodeError: 'ascii' codec can't encode characters` で失敗する
 *   (ロケール `C` で起動されるため)。この対応として、GeoJSON ファイル名は
 *   ASCII スラッグに正規化し、routes-master.json 側の日本語 (route_id /
 *   direction_name) はそのまま保持したまま、フロントで明示的にマップする。
 */
const ROUTE_SHAPE_SLUGS: Readonly<Record<string, string>> = {
  '京浜#北行': 'keihin-tohoku_north',
  '京浜#南行': 'keihin-tohoku_south',
  '中総緩#A線（千葉方面）': 'chuo-sobu-local_A_chiba',
  '中総緩#B線（三鷹方面）': 'chuo-sobu-local_B_mitaka',
};

/**
 * `(routeId, directionName)` から `/data/route_shapes/<slug>.geojson` の
 * スラッグ部分を解決する。マッピング未登録の路線は `null`。
 *
 * export しているのはユニットテスト用途。
 */
export function resolveRouteShapeSlug(routeId: string, directionName: string): string | null {
  if (!routeId || !directionName) return null;
  const key = `${routeId}#${directionName}`;
  return ROUTE_SHAPE_SLUGS[key] ?? null;
}

/**
 * 指定した route_id / direction_name に対応する線形 GeoJSON を取得する。
 *
 * - 404 / JSON パースエラーなど取得不可の場合は `null` を返す。
 *   呼び出し側 (地図) は `null` の時に線形を描画しないこと。
 * - マッピング (`ROUTE_SHAPE_SLUGS`) 未登録の路線もファイル未配布とみなし
 *   `null` を返す。
 *
 * @param routeId        例: "中総緩"
 * @param directionName  例: "A線（千葉方面）"
 */
export async function fetchRouteShapePoints(
  routeId: string,
  directionName: string,
): Promise<RouteShapePointsFile | null> {
  const slug = resolveRouteShapeSlug(routeId, directionName);
  if (!slug) return null;

  // slug は ASCII のみ (英数 + `-` + `_`) なので encodeURIComponent は不要だが、
  // 将来別の記号が混入しても壊れないよう念のためエンコードしておく。
  const url = `/data/route_shapes/${encodeURIComponent(slug)}.geojson`;

  let res: Response;
  try {
    res = await fetch(url, { cache: 'no-store' });
  } catch {
    return null;
  }
  if (!res.ok) return null;

  try {
    const json = (await res.json()) as RouteShapePointsFile;
    if (!json || json.type !== 'FeatureCollection' || !Array.isArray(json.features)) {
      return null;
    }
    return json;
  } catch {
    return null;
  }
}
